import type { ProjectIconName } from '@shared/project-icon';
import { fetchApi } from '@/lib/api';
import { factoryInit, queryString, type FactoryRequestOptions } from '@/lib/factory-request';
import type { CheckoutRequest, CheckoutStatus, GithubWebhookStatus, MachineCheckout, Project, ProjectGrant, ProjectMember, ProjectRole, ProjectSummary, RunMachineSuggestion, RunnableMachine, RunOpenPr } from '@shared/types/factory';

/** Projects, their access grants and checkouts (docs/first-slice-plan.md §3, "Projects"). */

export interface CreateProjectInput {
  name: string;
  slug?: string;
  repo_url: string;
  default_branch?: string;
  timezone?: string;
  /** Configures a checkout (admins). Null or left out: none. */
  setup_command?: string | null;
  /** Hex colour; left out, the project gets one picked from its slug. */
  color?: string;
  /** Two letters; left out, the slug's first free one (SF). */
  task_prefix?: string;
  icon?: ProjectIconName | null;
}

/** `setup_command` is admins only (403 otherwise). */
export type UpdateProjectInput = Partial<Pick<Project, 'name' | 'repo_url' | 'default_branch' | 'timezone' | 'setup_command' | 'color' | 'task_prefix' | 'icon' | 'kit_auto_merge' | 'worktree_tidy' | 'dev_idle_minutes'>>;

export type CreateGrantInput = { role: ProjectRole } & ({ user_id: string } | { organisation_id: string });

/** The projects the caller can see; archived ones only with `archived`. */
export function listProjects(opts: FactoryRequestOptions & { archived?: boolean } = {}) {
  return fetchApi<{ projects: ProjectSummary[] }>(
    `/projects${queryString({ archived: opts.archived ? 1 : undefined })}`,
    factoryInit(opts)
  );
}

/** Admins only. Seeds the default columns and task type. */
export function createProject(input: CreateProjectInput, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ project: Project }>('/projects', factoryInit(opts, 'POST', input));
}

/** The current project (the header) and the caller's role on it. 404 when unknown or not visible. */
export function getProject(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ project: Project; role: ProjectRole }>('/project', factoryInit(opts));
}

export function updateProject(input: UpdateProjectInput, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ project: Project }>('/project', factoryInit(opts, 'PATCH', input));
}

/** Admins only. */
export function archiveProject(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ project: Project }>('/project/archive', factoryInit(opts, 'POST'));
}

/** Everyone who can see the project: direct grants, organisation grants and admins. */
export function listMembers(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ members: ProjectMember[] }>('/project/members', factoryInit(opts));
}

export function listGrants(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ grants: ProjectGrant[] }>('/project/grants', factoryInit(opts));
}

export function createGrant(input: CreateGrantInput, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ grant: ProjectGrant }>('/project/grants', factoryInit(opts, 'POST', input));
}

export function updateGrant(grantId: string, role: ProjectRole, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ grant: ProjectGrant }>(
    `/project/grants/${encodeURIComponent(grantId)}`,
    factoryInit(opts, 'PATCH', { role })
  );
}

export function deleteGrant(grantId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<void>(`/project/grants/${encodeURIComponent(grantId)}`, factoryInit(opts, 'DELETE'));
}

/** Every machine's checkouts linked to or suggested for this project. */
export function listProjectCheckouts(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ checkouts: MachineCheckout[] }>('/project/checkouts', factoryInit(opts));
}

/**
 * Maintainers: confirm or ignore a checkout suggested for this project, or unlink it
 * (`linked: false`, with status 'ignored'). The admin Fleet page uses machines-api's updateCheckout.
 */
export function updateProjectCheckout(checkoutId: string, body: { status: CheckoutStatus; linked: boolean }, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ checkout: MachineCheckout }>(
    `/project/checkouts/${encodeURIComponent(checkoutId)}`,
    factoryInit(opts, 'PATCH', body)
  );
}

/**
 * Machines a run can use here: online, with a confirmed checkout of this project. With `taskId`, also the
 * machine the factory would use for that task (T-28), the Run dialog's default, and (SF-T-155) the open PR a
 * new build of it takes over.
 */
export function listRunnableMachines(opts: FactoryRequestOptions = {}, taskId?: string) {
  const query = taskId ? `?task_id=${encodeURIComponent(taskId)}` : '';
  return fetchApi<{ machines: RunnableMachine[]; suggested?: RunMachineSuggestion | null; open_pr?: RunOpenPr | null }>(`/project/machines${query}`, factoryInit(opts));
}

/** Maintainers: the machines asked to check this project out, and how each went. */
export function listCheckoutRequests(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ requests: CheckoutRequest[] }>('/project/checkout-requests', factoryInit(opts));
}

/**
 * Admins: ask machines to check the project out (clone or adopt, register in T3 Code, run the setup
 * command). Asking again re-runs it. Online machines get it at once, offline ones when they connect.
 */
export function createCheckoutRequests(machineIds: string[], opts: FactoryRequestOptions = {}) {
  return fetchApi<{ requests: CheckoutRequest[] }>(
    '/project/checkout-requests',
    factoryInit(opts, 'POST', { machine_ids: machineIds })
  );
}

/** GitHub's webhook for the project's repo (T-13): secret set or not, the Payload URL, the last delivery. */
export function getGithubWebhook(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ webhook: GithubWebhookStatus }>('/project/github-webhook', factoryInit(opts));
}
